import React, { useEffect, useRef, useCallback, useState } from 'react';

// Minimal Leaflet-via-CDN loader (same CDN + guard as CompMap.jsx; skips work if Leaflet is loaded).
let leafletLoading = null;
function injectCss(id, href) {
  if (document.querySelector('#' + id)) return;
  const link = document.createElement('link');
  link.id = id; link.rel = 'stylesheet'; link.href = href;
  document.head.appendChild(link);
}
function loadScript(src, id) {
  return new Promise((resolve) => {
    if (id && document.querySelector('#' + id)) return resolve();
    const s = document.createElement('script');
    if (id) s.id = id;
    s.src = src; s.onload = resolve; s.onerror = resolve;
    document.head.appendChild(s);
  });
}
function loadLeaflet() {
  if (window.L && window.L.map) return Promise.resolve(window.L);
  if (leafletLoading) return leafletLoading;
  leafletLoading = (async () => {
    injectCss('leaflet-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css');
    if (!window.L) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js', 'leaflet-js');
    for (let i = 0; i < 60 && !(window.L && window.L.map); i++) await new Promise(r => setTimeout(r, 40));
    return window.L;
  })();
  return leafletLoading;
}

const STREET_TILES = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const SAT_TILES    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const STREET_ATTR  = '© OpenStreetMap contributors © CARTO';

// Pin color by pipeline stage.
const STAGE_COLOR = {
  'Available': '#14504A', 'Tagged': '#5B8DB8', 'Working': '#5B8DB8', 'Offer Sent': '#E0A526',
  'Under Contract': '#8B5CF6', 'Offer Accepted': '#8B5CF6', 'Sold': '#4A7C59',
  'Closed': '#4A7C59', 'Dropped': '#9C9690', 'Dead': '#9C9690',
};
function stageColor(stage) { return STAGE_COLOR[stage] || '#5B8DB8'; }
function fmtK(n) { return n ? '$' + Math.round(n / 1000) + 'k' : '—'; }

// Map view of a set of deals. Pins are placed for deals with lat/lng, colored by stage. Clicking a
// pin selects that deal (onSelect). The selected pin is enlarged.
export default function DealsMap({ deals = [], selectedId = null, onSelect, height = 560 }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersRef = useRef([]);
  const onSelectRef = useRef(onSelect);
  const [tileType, setTileType] = useState('street');
  onSelectRef.current = onSelect;

  const withCoords = deals.filter(d => Number(d.lat) && Number(d.lng));

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then(L => {
      if (cancelled || !mapDivRef.current || mapRef.current || !L) return;
      const map = L.map(mapDivRef.current, {
        zoomControl: true, attributionControl: false, scrollWheelZoom: false, doubleClickZoom: true,
      }).setView([30.267, -97.743], 11);
      tileLayerRef.current = L.tileLayer(STREET_TILES, { attribution: STREET_ATTR, detectRetina: true, maxZoom: 19 }).addTo(map);
      mapRef.current = map;
      renderMarkers(L);
    });
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current) return;
    if (tileLayerRef.current) tileLayerRef.current.remove();
    const opts = tileType === 'satellite'
      ? { attribution: '© Esri World Imagery', maxZoom: 19 }
      : { attribution: STREET_ATTR, detectRetina: true, maxZoom: 19 };
    tileLayerRef.current = L.tileLayer(tileType === 'satellite' ? SAT_TILES : STREET_TILES, opts).addTo(mapRef.current);
  }, [tileType]);

  const renderMarkers = useCallback((LRef) => {
    const L = LRef || window.L;
    if (!L || !mapRef.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    function pinIcon({ color, size = 16, selected = false }) {
      const cap = size + (selected ? 10 : 4);
      return L.divIcon({
        className: '',
        html:
          `<div style="position:relative;width:${cap}px;height:${cap}px;display:flex;align-items:center;justify-content:center;">` +
            (selected ? `<div style="position:absolute;inset:0;border-radius:50%;background:rgba(224,165,38,0.35);"></div>` : '') +
            `<div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.4);"></div>` +
          `</div>`,
        iconSize: [cap, cap], iconAnchor: [cap / 2, cap / 2],
      });
    }

    for (const d of withCoords) {
      const isSel = selectedId != null && d.id === selectedId;
      const marker = L.marker([Number(d.lat), Number(d.lng)], {
        icon: pinIcon({ color: stageColor(d.stage), size: isSel ? 20 : 15, selected: isSel }),
        zIndexOffset: isSel ? 1000 : 0,
      }).addTo(mapRef.current);
      const offer = d.offer_amount != null ? `Offer ${fmtK(d.offer_amount)}` : (d.max_offer != null ? `MAO ${fmtK(d.max_offer)}` : '');
      const tip = `<div style="font-weight:600">${d.address || ''}</div>` +
        `<div style="opacity:.8">${[d.city, d.state, d.zip].filter(Boolean).join(', ')}</div>` +
        `<div>ARV ${fmtK(d.ai_arv)}${offer ? ' · ' + offer : ''}</div>` +
        `<div style="opacity:.7">${d.stage || ''}</div>`;
      marker.bindTooltip(tip, { direction: 'top', offset: [0, -10] });
      marker.on('click', () => { if (onSelectRef.current) onSelectRef.current(d); });
      markersRef.current.push(marker);
    }

    const pts = withCoords.map(d => [Number(d.lat), Number(d.lng)]);
    if (pts.length >= 2) {
      const bounds = L.latLngBounds(pts).pad(0.2);
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { maxZoom: 14 });
    } else if (pts.length === 1) {
      mapRef.current.setView(pts[0], 13);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, selectedId]);

  useEffect(() => { renderMarkers(); }, [renderMarkers]);

  const missing = deals.length - withCoords.length;

  return (
    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--navy-border)' }}>
      <div ref={mapDivRef} style={{ width: '100%', height }} />
      {withCoords.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,30,40,0.35)', color: 'var(--white)', fontSize: 13, textAlign: 'center', padding: 20 }}>
          None of these deals have map coordinates yet.<br />Deals sourced from MLS/off-market with a location will appear here.
        </div>
      )}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000, display: 'flex', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 6, overflow: 'hidden' }}>
        {['street', 'satellite'].map(t => (
          <button key={t} onClick={() => setTileType(t)} style={{
            padding: '4px 10px', fontSize: 10, fontWeight: 600,
            background: tileType === t ? 'var(--teal)' : 'transparent',
            color: tileType === t ? '#fff' : 'var(--gray-300)', border: 'none', cursor: 'pointer', textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>
      {missing > 0 && (
        <div style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1000, padding: '5px 9px', background: 'rgba(250, 247, 242,0.85)', border: '1px solid var(--navy-border)', borderRadius: 6, fontSize: 10, color: 'var(--gray-300)' }}>
          {missing} deal{missing > 1 ? 's' : ''} without coordinates not shown
        </div>
      )}
    </div>
  );
}
